import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import Header from 'on_web/components/shared/Menu/Header/Header';
import ReportsBottomNavigation from 'on_web/components/shared/ReportsBottomNavigation/ReportsBottomNavigation';
import RedirectPanelWithIcon from 'on_web/components/shared/RedirectPanelWithIcon/RedirectPanelWithIcon';
import PageItemContainer from 'on_web/components/shared/Common/PageItemContainer/PageItemContainer';
import DesktopMenu from 'on_web/components/shared/Menu/DesktopMenu/DesktopMenu';
import HealthBasedRiskSummary from 'on_web/components/shared/HealthBasedRiskSummary/HealthBasedRiskSummary';
import RisksOnHumanBody from 'on_web/components/shared/RisksOnHumanBody/RisksOnHumanBody';
import ErrorMessageTile from 'on_web/components/shared/ErrorMessageTile/ErrorMessageTile';
import CustomDialog from 'on_web/components/shared/CustomDialog/CustomDialog';

import { ROUTE_ASSESSMENT_HEALTH_RISKS_GROUPS } from 'routes';

import useUsersApi from 'on_web/hooks/useUsersApi';
import useCheckScreenSize from 'on_web/hooks/useCheckScreenSize';
import useInterval from 'on_web/hooks/useInterval';
import { reportsBottomNavigationOptions } from 'on_web/helpers/redirectHelper';
import { APP_PRIMARY } from 'colours';

import ReportsOpenTextSection from 'on_web/components/shared/ReportsOpenTextSection/ReportsOpenTextSection';
import { getUserMetadata } from '../pce_legacy/src/helpers/clientStorageHelper';
import { mapRoutesToMenuItems } from 'on_web/helpers/menuItems';
import {
  getHealthRisks,
  getHealthRiskScore,
  getHealthRiskScorePercentage,
  parseHealthRisks,
  parseLikelihood,
} from 'on_web/helpers/risksHelper';
import { Divider } from '@material-ui/core';
import RiskReport from 'on_web/components/shared/RiskReport/RiskReport';

export default function ReportsComorbiditiesOverview(props) {
  const { t } = useTranslation();
  const comorbidityCategories = getHealthRisks(APP_PRIMARY, '60px');

  const [isDesktop] = useCheckScreenSize();

  const [
    { getSpareAssessments, errorSpareAssessments, responseReports, getReports },
  ] = useUsersApi();
  const [responseHealthBasedRisks, setResponseHealthBasedRisks] = useState(
    null
  );
  const [healthBasedRisksObj, setHealthBasedRisksObj] = useState(null);
  const [likelihoodObj, setLikelihoodObj] = useState(null);
  const [responseLikelihood, setResponseLikelihood] = useState(null);
  const [responseDnaBasedRisks, setResponseDnaBasedRisks] = useState(null);

  useEffect(() => {
    if (
      props.location.state &&
      props.location.state.healthBasedRisks &&
      props.location.state.infectionLikelihood &&
      props.location.state.dnaBasedRisks
    ) {
      setResponseHealthBasedRisks(props.location.state.healthBasedRisks);
      setHealthBasedRisksObj(
        parseHealthRisks(props.location.state.healthBasedRisks)
      );
      setResponseLikelihood(props.location.state.infectionLikelihood);
      setLikelihoodObj(
        parseLikelihood(props.location.state.infectionLikelihood, t)
      );
      setResponseDnaBasedRisks(props.location.state.dnaBasedRisks);
    } else {
      getSpareAssessments().then(resp => {
        if (resp) {
          setResponseHealthBasedRisks(resp.health_based_severity);
          setHealthBasedRisksObj(parseHealthRisks(resp.health_based_severity));
          setResponseLikelihood(resp.past_infection_likelihood);
          setLikelihoodObj(parseLikelihood(resp.past_infection_likelihood, t));
          setResponseDnaBasedRisks(resp.dna_based_severity);
        }
      });
    }

    getReports();
  }, [getSpareAssessments, getReports]);

  useInterval(() => {
    if (responseReports == null || responseReports.download_url == null) {
      getReports();
    }
  }, 5000);

  const comorbiditiesWithRisk = (risks, riskKeys) => {
    let comorbidities = [];
    for (const riskKeyIx in riskKeys) {
      risks.forEach(risk => {
        if (risk.risk.code === riskKeys[riskKeyIx] && risk.level >= 1) {
          comorbidities.push(risk);
        }
      });
    }

    return comorbidities;
  };

  const getChartData = () => {
    if (responseHealthBasedRisks == null) {
      return [];
    }

    const ageGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'age'
    );
    const respirtoryGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'respiratory'
    );
    const bmiDiabetesGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'bmi_diabetes'
    );
    const cardiacGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'cardiac'
    );
    const kidneyGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'kidney'
    );
    const autoimmuneGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'autoimmune'
    );
    const cancerGroup = responseHealthBasedRisks.groups.find(
      g => g.code === 'cancer'
    );

    const ageRisk = responseHealthBasedRisks.risks.find(
      r => r.risk.code === 'age_covid_19_risk'
    );

    return [
      {
        label: ageGroup.name,
        subLabel: `${ageRisk.level_label} Risk`,
        value: ageRisk.level,
        code: 'age',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          ageGroup.risks
        ).length,
        risksCount: ageGroup.risks.length,
        score: ageRisk.score,
        useScore: true,
      },
      {
        label: respirtoryGroup.name,
        subLabel:
          comorbiditiesWithRisk(
            responseHealthBasedRisks.risks,
            respirtoryGroup.risks
          ).length === 1
            ? '1 Risk Factor'
            : comorbiditiesWithRisk(
                responseHealthBasedRisks.risks,
                respirtoryGroup.risks
              ).length + ' Risk Factors',
        value: -1,
        code: 'respiratory',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          respirtoryGroup.risks
        ).length,
        risksCount: respirtoryGroup.risks.length,
      },
      {
        label: bmiDiabetesGroup.name,
        subLabel:
          comorbiditiesWithRisk(
            responseHealthBasedRisks.risks,
            bmiDiabetesGroup.risks
          ).length === 1
            ? '1 Risk Factor'
            : comorbiditiesWithRisk(
                responseHealthBasedRisks.risks,
                bmiDiabetesGroup.risks
              ).length + ' Risk Factors',
        value: -1,
        code: 'bmi_diabetes',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          bmiDiabetesGroup.risks
        ).length,
        risksCount: bmiDiabetesGroup.risks.length,
      },
      {
        label: cardiacGroup.name,
        subLabel:
          comorbiditiesWithRisk(
            responseHealthBasedRisks.risks,
            cardiacGroup.risks
          ).length === 1
            ? '1 Risk Factor'
            : comorbiditiesWithRisk(
                responseHealthBasedRisks.risks,
                cardiacGroup.risks
              ).length + ' Risk Factors',
        value: -1,
        code: 'cardiac',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          cardiacGroup.risks
        ).length,
        risksCount: cardiacGroup.risks.length,
      },
      {
        label: kidneyGroup.name,
        subLabel:
          comorbiditiesWithRisk(
            responseHealthBasedRisks.risks,
            kidneyGroup.risks
          ).length === 1
            ? '1 Risk Factor'
            : comorbiditiesWithRisk(
                responseHealthBasedRisks.risks,
                kidneyGroup.risks
              ).length + ' Risk Factors',
        value: -1,
        code: 'kidney',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          kidneyGroup.risks
        ).length,
        risksCount: kidneyGroup.risks.length,
      },
      {
        label: autoimmuneGroup.name,
        subLabel:
          comorbiditiesWithRisk(
            responseHealthBasedRisks.risks,
            autoimmuneGroup.risks
          ).length === 1
            ? '1 Risk Factor'
            : comorbiditiesWithRisk(
                responseHealthBasedRisks.risks,
                autoimmuneGroup.risks
              ).length + ' Risk Factors',
        value: -1,
        code: 'autoimmune',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          autoimmuneGroup.risks
        ).length,
        risksCount: autoimmuneGroup.risks.length,
      },
      {
        label: cancerGroup.name,
        subLabel:
          comorbiditiesWithRisk(
            responseHealthBasedRisks.risks,
            cancerGroup.risks
          ).length === 1
            ? '1 Risk Factor'
            : comorbiditiesWithRisk(
                responseHealthBasedRisks.risks,
                cancerGroup.risks
              ).length + ' Risk Factors',
        value: -1,
        code: 'cancer',
        risksNumber: comorbiditiesWithRisk(
          responseHealthBasedRisks.risks,
          cancerGroup.risks
        ).length,
        risksCount: cancerGroup.risks.length,
      },
    ];
  };

  const getStateToTransfer = () => {
    return {
      ...props.location.state,
      healthBasedRisks: responseHealthBasedRisks,
      infectionLikelihood: responseLikelihood,
      dnaBasedRisks: responseDnaBasedRisks,
    };
  };

  return (
    <div style={{ display: 'flex' }}>
      {errorSpareAssessments ? (
        <CustomDialog
          open={errorSpareAssessments}
          onCloseButton={() => {
            window.location = '/';
          }}
          style={{ marginLeft: isDesktop ? '300px' : '0' }}
        >
          <ErrorMessageTile
            level="error"
            title={t('common.errorMessages.general.title')}
            message={t('common.errorMessages.general.message')}
            actionText={t('common.errorMessages.general.reloadAction')}
            onButtonClick={() => {
              window.location = '/';
            }}
          ></ErrorMessageTile>
        </CustomDialog>
      ) : (
        <>
          <DesktopMenu
            id={1}
            stateToTransferOnNavigation={getStateToTransfer()}
          />
          <Grid
            container={isDesktop}
            direction="row"
            justify="center"
            alignItems="center"
            style={{
              width: `calc(100% - ${isDesktop ? 300 : 0}px)`,
            }}
          >
            <PageItemContainer>
              {isDesktop === false && (
                <Header
                  squeeze
                  title={t('reporting.myAssessment.healthBasedAssessment')}
                  secondaryHeader
                  hasShareAction
                  mainMenuItems={t('common.menu.mainItems', {
                    returnObjects: true,
                  })}
                  extraMenuItems={mapRoutesToMenuItems(getUserMetadata())}
                />
              )}
              {responseHealthBasedRisks && (
                <>
                  <Grid
                    container
                    direction={isDesktop ? 'row' : 'column'}
                    justify="center"
                    alignItems="stretch"
                  >
                    <Grid item xs={12} md={6}>
                      {isDesktop === false && <Divider />}
                      <div
                        style={{
                          paddingLeft: '20px',
                          paddingRight: '20px',
                          paddingTop: '20px',
                          paddingBottom: '22px',
                        }}
                      >
                        <HealthBasedRiskSummary
                          isDesktop={isDesktop}
                          healthRiskScorePercentage={getHealthRiskScorePercentage(
                            getHealthRiskScore(responseHealthBasedRisks)
                          )}
                          healthRiskValue={
                            responseHealthBasedRisks.total_risk.level
                          }
                          healthRiskText={
                            responseHealthBasedRisks.total_risk.level_label
                          }
                        />
                      </div>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        style={{
                          borderBottom: isDesktop
                            ? 'none'
                            : '1px Solid #efefef',
                          marginTop: isDesktop ? '10px' : '-10px',
                        }}
                      >
                        <RisksOnHumanBody
                          title={t('reporting.comorbiditiesOverview.title')}
                          data={getChartData()}
                          isDesktop={isDesktop}
                          onLabelClicked={label => {
                            props.history.push({
                              pathname: ROUTE_ASSESSMENT_HEALTH_RISKS_GROUPS,
                              state: {
                                group: label,
                                ...getStateToTransfer(),
                              },
                            });
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider />

                  <Box
                    style={{
                      ...(isDesktop && { borderBottom: '1px Solid #efefef' }),
                    }}
                  >
                    <ReportsOpenTextSection
                      title={''}
                      paragraphsKeys={responseHealthBasedRisks.total_risk.recommendations.map(
                        r => {
                          return r;
                        }
                      )}
                    />
                  </Box>
                  {isDesktop === false && (
                    <Box
                      style={{
                        borderBottom: '1px Solid #efefef',
                      }}
                    >
                      {comorbidityCategories.map((comorbidityCategory, ix) => {
                        return (
                          <RedirectPanelWithIcon
                            key={ix}
                            title={responseHealthBasedRisks.groups
                              .find(g => g.code === comorbidityCategory.code)
                              .name.toUpperCase()}
                            icon={comorbidityCategory.icon}
                            titleColor={'#2fb9da'}
                            onClick={() => {
                              props.history.push({
                                pathname: ROUTE_ASSESSMENT_HEALTH_RISKS_GROUPS,
                                state: {
                                  group: comorbidityCategory.code,
                                  ...getStateToTransfer(),
                                },
                              });
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  <RiskReport report={responseReports} />
                </>
              )}
              {!isDesktop && (
                <ReportsBottomNavigation
                  selected={reportsBottomNavigationOptions.healthRisk}
                  onSelected={val =>
                    props.history.push({
                      pathname: val,
                      state: getStateToTransfer(),
                    })
                  }
                />
              )}
            </PageItemContainer>
          </Grid>
        </>
      )}
    </div>
  );
}
